import { useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { ArrowLeft, Plus, Ban, Lock } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { PlayerDetailPanel } from '@/components/players/PlayerDetailPanel';
import { AddPlayerForm } from '@/components/players/AddPlayerForm';
import { PlayerCardList } from '@/components/players/PlayerCardList';
import { PlayerProfileViewerModal } from '@/components/players/PlayerProfileViewerModal';
import { PlayersPageStaleBanner } from '@/components/players/PlayersPageStaleBanner';
import { PlayerTable } from '@/components/players/PlayerTable';
import { playerDisplayName } from '@/components/players/player-utils';
import { usePlayersController } from './players/usePlayersController';
import { InviteLocaleSelect } from '@/components/InviteLocaleSelect';
import { useHubOutletContext } from './tournament-hub/TournamentHubLayoutRoute';

export function PlayersPage() {
  const { t } = useTranslation('director');
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  // Hub provides the lifecycle controller + hub-wide God Mode (story
  // 083). The Players page hosts the close-registration shortcut so the
  // director doesn't have to bounce back to the hub overview to advance
  // the phase; God Mode (force-add etc.) comes from the header toggle.
  const { tournament: hubTournament, lifecycle, godMode } = useHubOutletContext();
  const ctrl = usePlayersController(id, navigate, godMode);
  async function closeRegistrationFromPlayers() {
    await lifecycle.handleCloseRegistration();
    await ctrl.reload();
  }
  // The close-registration dialog (pending-players "approve and close"
  // / "reject and close") finalises asynchronously after the click on
  // the Players page returns. It reloads the layout's tournament copy,
  // but this page has its own roster + tournament fetched by
  // usePlayersController. When the hub status drifts away from ours,
  // refresh — that catches the approve-and-close path that updates
  // pending → accepted server-side.
  const hubStatus = hubTournament?.status;
  const localStatus = ctrl.tournament?.status;
  useEffect(() => {
    if (!hubStatus || !localStatus) return;
    if (hubStatus !== localStatus) {
      void ctrl.reload();
    }
    // ctrl.reload is stable for the lifetime of the controller; eslint
    // would otherwise want it in the dep array and re-fire the effect
    // whenever any controller-internal state updates.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hubStatus, localStatus]);
  const {
    tournament,
    loading,
    notesTarget,
    setNotesTarget,
    notesText,
    setNotesText,
    selectedPlayer,
    setSelectedPlayer,
    showAddForm,
    setShowAddForm,
    handleAddPlayerMobile,
    handleStatusChange,
    handleUpdatePlayer,
    requestRemovePlayer,
    blockConfirmTarget,
    cancelBlockConfirm,
    confirmBlockAndRemove,
    handleSaveNotes,
    handleRefreshFromProfile,
    handleUnblockPlayer,
    messagePlayer,
    openInviteDialog,
    inviteTarget,
    closeInviteDialog,
    inviteEmail,
    setInviteEmail,
    inviteLocale,
    setInviteLocale,
    sendingInvite,
    handleSendInvite,
    rejectTarget,
    closeRejectDialog,
    rejectReason,
    setRejectReason,
    rejectBlock,
    setRejectBlock,
    submittingReject,
    handleSubmitReject,
    isRegistrationOpen,
    canEdit,
    maxReached,
    canAdd,
    playerCountText,
    viewMode,
    blockedCount,
    switchView,
    staleSince,
    refreshFromStaleBanner,
    viewProfileTarget,
    viewProfileData,
    viewProfileLoading,
    closeProfileViewer,
  } = ctrl;

  if (loading) {
    return (
      <div
        data-testid="players-page-loading"
        className="flex min-h-screen items-center justify-center"
      >
        <p>{t('players_page.loading')}</p>
      </div>
    );
  }

  return (
    <div
      data-testid="players-page"
      className="w-full md:flex md:min-h-0 md:flex-1 md:flex-col"
    >
      <main className="mx-auto w-full max-w-6xl md:flex md:min-h-0 md:flex-1 md:flex-col md:overflow-hidden">
        {staleSince && (
          <PlayersPageStaleBanner onRefresh={() => void refreshFromStaleBanner()} />
        )}

        <div className="mb-6 flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
          <div>
            <h2 className="text-xl font-semibold md:text-2xl">
              {t('players_page.title_suffix', { name: tournament?.name ?? '' })}
            </h2>
            <p data-testid="player-count" className="text-sm text-muted-foreground">
              {playerCountText}
            </p>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            {canAdd && (
              <Button
                data-testid="mobile-add-player-button"
                className="md:hidden"
                size="sm"
                onClick={() => setShowAddForm(true)}
              >
                <Plus className="mr-1 h-4 w-4" />
                {t('players_page.add_button_mobile')}
              </Button>
            )}
            {viewMode === 'default' && blockedCount > 0 && (
              <Button
                variant="outline"
                size="sm"
                data-testid="blocked-players-toggle"
                onClick={() => void switchView('blocked')}
              >
                <Ban className="mr-1 h-4 w-4" />
                {t('players_page.blocked_button', { count: blockedCount })}
              </Button>
            )}
            {viewMode === 'blocked' && (
              <Button
                variant="outline"
                size="sm"
                data-testid="back-to-default-list"
                onClick={() => void switchView('default')}
              >
                <ArrowLeft className="mr-1 h-4 w-4" />
                {t('players_page.back_to_default_list')}
              </Button>
            )}
            {isRegistrationOpen && (
              <Button
                variant="outline"
                size="sm"
                data-testid="players-page-close-registration-button"
                onClick={() => void closeRegistrationFromPlayers()}
              >
                <Lock className="mr-1 h-4 w-4" />
                {t('players_page.close_registration')}
              </Button>
            )}
          </div>
        </div>

        {viewMode === 'blocked' && (
          <div
            className="mb-4 rounded border border-amber-300 bg-amber-50 px-4 py-2"
            data-testid="blocked-view-banner"
          >
            <p className="text-sm font-medium text-amber-900">
              {blockedCount === 1
                ? t('players_page.blocked_view_banner_singular', { count: blockedCount })
                : t('players_page.blocked_view_banner_plural', { count: blockedCount })}
            </p>
          </div>
        )}

        {isRegistrationOpen && maxReached && (
          <div className="mb-4">
            <p
              data-testid="max-players-reached"
              className="text-sm text-muted-foreground"
            >
              {t('players_page.max_reached')}
            </p>
          </div>
        )}

        <PlayerCardList ctrl={ctrl} />

        {id && <PlayerTable ctrl={ctrl} tournamentId={id} />}
      </main>

      <PlayerProfileViewerModal
        open={viewProfileTarget !== null}
        loading={viewProfileLoading}
        profile={viewProfileData}
        onClose={closeProfileViewer}
      />

      <Dialog
        open={rejectTarget !== null}
        onOpenChange={(open) => !open && closeRejectDialog()}
      >
        <DialogContent data-testid="reject-dialog">
          <DialogHeader>
            <DialogTitle>
              {t('players_page.reject_dialog.title', {
                name: rejectTarget ? playerDisplayName(rejectTarget) : '',
              })}
            </DialogTitle>
          </DialogHeader>
          <p className="text-sm text-muted-foreground">
            {t('players_page.reject_dialog.body')}
          </p>
          <Textarea
            data-testid="reject-reason-input"
            value={rejectReason}
            onChange={(e) => setRejectReason(e.target.value)}
            placeholder={t('players_page.reject_dialog.reason_placeholder')}
            rows={3}
          />
          <label className="flex items-center gap-2 text-sm">
            <input
              type="checkbox"
              data-testid="reject-block-checkbox"
              checked={rejectBlock}
              onChange={(e) => setRejectBlock(e.target.checked)}
              className="h-4 w-4"
            />
            <span>{t('players_page.reject_dialog.block_label')}</span>
          </label>
          <DialogFooter>
            <Button variant="outline" onClick={closeRejectDialog}>
              {t('players_page.reject_dialog.cancel_button')}
            </Button>
            <Button
              variant="destructive"
              data-testid="reject-submit-button"
              disabled={submittingReject}
              onClick={handleSubmitReject}
            >
              {submittingReject
                ? rejectBlock
                  ? t('players_page.reject_dialog.blocking')
                  : t('players_page.reject_dialog.rejecting')
                : rejectBlock
                  ? t('players_page.reject_dialog.submit_block')
                  : t('players_page.reject_dialog.submit_reject')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog
        open={blockConfirmTarget !== null}
        onOpenChange={(open) => !open && cancelBlockConfirm()}
      >
        <DialogContent data-testid="block-confirm-dialog">
          <DialogHeader>
            <DialogTitle>
              {t('players_page.block_confirm.title', {
                name: blockConfirmTarget
                  ? playerDisplayName(blockConfirmTarget)
                  : '',
              })}
            </DialogTitle>
          </DialogHeader>
          <p className="text-sm text-muted-foreground">
            {t('players_page.block_confirm.body')}
          </p>
          <DialogFooter>
            <Button variant="outline" onClick={cancelBlockConfirm}>
              {t('players_page.block_confirm.cancel_button')}
            </Button>
            <Button
              variant="destructive"
              data-testid="block-confirm-button"
              onClick={confirmBlockAndRemove}
            >
              {t('players_page.block_confirm.confirm_button')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog
        open={inviteTarget !== null}
        onOpenChange={(open) => !open && closeInviteDialog()}
      >
        <DialogContent data-testid="invite-dialog">
          <DialogHeader>
            <DialogTitle>
              {t('players_page.invite_dialog.title', {
                name: inviteTarget ? playerDisplayName(inviteTarget) : '',
              })}
            </DialogTitle>
          </DialogHeader>
          <p className="text-sm text-muted-foreground">
            {t('players_page.invite_dialog.body')}
          </p>
          <Input
            data-testid="invite-email-input"
            type="email"
            autoFocus
            value={inviteEmail}
            onChange={(e) => setInviteEmail(e.target.value)}
            placeholder={t('players_page.invite_dialog.email_placeholder')}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && !sendingInvite) handleSendInvite();
            }}
          />
          <InviteLocaleSelect
            value={inviteLocale}
            onChange={setInviteLocale}
            labelText={t('players_page.invite_dialog.language_label')}
            hintText={t('players_page.invite_dialog.language_hint')}
          />
          <DialogFooter>
            <Button variant="outline" onClick={closeInviteDialog}>
              {t('players_page.invite_dialog.cancel_button')}
            </Button>
            <Button
              data-testid="invite-send-button"
              disabled={sendingInvite || inviteEmail.trim().length === 0}
              onClick={handleSendInvite}
            >
              {sendingInvite
                ? t('players_page.invite_dialog.sending')
                : t('players_page.invite_dialog.send_button')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog
        open={notesTarget !== null}
        onOpenChange={(open) => !open && setNotesTarget(null)}
      >
        <DialogContent data-testid="notes-dialog">
          <DialogHeader>
            <DialogTitle>
              {t('players_page.notes_dialog.title', {
                name: notesTarget ? playerDisplayName(notesTarget) : '',
              })}
            </DialogTitle>
          </DialogHeader>
          <Textarea
            data-testid="notes-input"
            value={notesText}
            onChange={(e) => setNotesText(e.target.value)}
            placeholder={t('players_page.notes_dialog.placeholder')}
            rows={4}
          />
          <DialogFooter>
            <Button variant="outline" onClick={() => setNotesTarget(null)}>
              {t('players_page.notes_dialog.cancel_button')}
            </Button>
            <Button data-testid="save-notes-button" onClick={handleSaveNotes}>
              {t('players_page.notes_dialog.save_button')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {selectedPlayer && (
        <PlayerDetailPanel
          player={selectedPlayer}
          canEdit={canEdit}
          isRegistrationOpen={isRegistrationOpen}
          onClose={() => setSelectedPlayer(null)}
          onUpdate={handleUpdatePlayer}
          onStatusChange={handleStatusChange}
          onRemove={requestRemovePlayer}
          onInvite={(p) => {
            setSelectedPlayer(null);
            openInviteDialog(p);
          }}
          onRefreshFromProfile={handleRefreshFromProfile}
          onMessage={(p) => {
            setSelectedPlayer(null);
            messagePlayer(p);
          }}
          onUnblock={handleUnblockPlayer}
          gameId={tournament?.game ?? 'chess'}
        />
      )}

      {showAddForm && (
        <AddPlayerForm
          onAdd={handleAddPlayerMobile}
          onClose={() => setShowAddForm(false)}
          gameId={tournament?.game ?? 'chess'}
        />
      )}
    </div>
  );
}
